import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Trans, withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import map from 'lodash/fp/map'
import join from 'lodash/fp/join'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'
import urlFor from 'shared/tools/url-helper'
import deviceHelper from 'shared/tools/device-helper'

import UserSubHeaderBox from 'client/bookmate/boxes/user-sub-header-box'
import UserSubHeaderBooksBox from 'client/bookmate/boxes/user-sub-header-books-box'
import HeaderBox from 'client/shared/boxes/header-box'
import UserInfoBox from 'client/bookmate/boxes/user-info-box'

import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import { Heading } from 'client/shared/blocks/heading'
import { LayoutColumn } from 'client/shared/blocks/layout-column'
import LayoutUserSection from 'client/shared/blocks/layout-user-section'
import { UserSectionNav } from 'client/bookmate/blocks/user-section-nav'
import { Tab } from 'client/shared/blocks/tab'
import Spacer from 'client/shared/blocks/spacer'
import List from 'client/bookmate/blocks/list'
import { State as AppState } from 'client/shared/reducers/app-reducer'

import { buildUser } from 'client/bookmate/reducers/user-reducer'

import {
  loadShelves,
  loadShelvesFollowing,
} from 'client/bookmate/reducers/user-reducer'

import { getShelvesByIds } from 'client/bookmate/reducers/shelves-reducer'

import Shelf from 'client/bookmate/blocks/shelf'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'
import { Dispatch, State } from 'shared/types/redux'
import { ShelfProps } from 'client/shared/types/shelf'

const getShelvesTitles = compose(join(', '), map('title'))

export const SHELVES_LIMIT = 10

type Props = {
  currentUserLogin: string
  dispatch: Dispatch
  params: Params
  user: {
    login: string
    pages: Record<string, number[]>
    myself: boolean
    name: string
    counters: Record<string, number>
    id: number
  }
  shelves: string[] | ShelfProps[]
  app: AppState
  routeParams: Params
} & withI18nProps

class UserShelvesBox extends Component<Props> {
  load = () => {
    const {
      dispatch,
      user: { login, pages },
      params: { filter },
    } = this.props

    const shelvesPage = pages.shelves[filter]

    if (login) {
      if (filter === 'following') {
        return dispatch(
          loadShelvesFollowing(login, shelvesPage, SHELVES_LIMIT, true),
        )
      } else {
        return dispatch(loadShelves(login, shelvesPage, SHELVES_LIMIT, true))
      }
    }
  }

  getLocaleByFilter() {
    const {
      user: { myself },
      params: { filter },
    } = this.props

    const localeByFilter = {
      created: 'shelves',
      following: 'shelves_following',
    }

    return myself
      ? `own_${localeByFilter[filter]}`
      : `other_${localeByFilter[filter]}`
  }

  render() {
    const {
      app,
      user,
      user: { myself },
    } = this.props

    return (
      <>
        {this.renderMeta()}
        <HeaderBox />
        {myself ? <UserSubHeaderBooksBox /> : <UserInfoBox user={user} />}
        <UserSubHeaderBox hidden={myself} />
        <Spacer size={32} />
        {deviceHelper.isDesktopAllSizes(app)
          ? this.renderDesktop()
          : this.renderMobile()}
      </>
    )
  }

  renderMeta() {
    const {
      user: { name, login: username },
      shelves,
      i18n,
    } = this.props
    const title = i18n._('profile.meta_title_bookshelves', { name, username })
    const description = getShelvesTitles(shelves)

    return (
      <>
        <MetaTitle title={title} />
        <MetaDescription description={description} />
      </>
    )
  }

  renderDesktop() {
    return (
      <LayoutUserSection
        firstColumn={<>{this.renderNav('column')}</>}
        secondColumn={
          <div>
            <Heading looksLike={2}>
              <Trans id={`profile.${this.getLocaleByFilter()}`} />
            </Heading>
            {this.renderShelves()}
            <Spacer size="bottom-page" />
          </div>
        }
      />
    )
  }

  renderMobile() {
    return (
      <LayoutColumn>
        {this.renderNav('compact')}
        {this.renderShelves()}
        <Spacer size="bottom-page" />
      </LayoutColumn>
    )
  }

  renderNav(tabKind) {
    const {
      app,
      user: {
        myself,
        login,
        counters: { bookshelves, following_bookshelves },
      },
    } = this.props

    return (
      <UserSectionNav>
        <Tab
          path={urlFor.userShelves(login, app.storedQuery)}
          count={bookshelves}
          kind={tabKind}
        >
          <Trans
            id={`${myself ? 'profile.own_shelves' : 'profile.other_shelves'}`}
          />
        </Tab>
        <Tab
          path={urlFor.userShelvesFollowing(login, app.storedQuery)}
          count={following_bookshelves}
          kind={tabKind}
        >
          <Trans
            id={`${
              myself
                ? 'profile.own_shelves_following'
                : 'profile.other_shelves_following'
            }`}
          />
        </Tab>
      </UserSectionNav>
    )
  }

  renderShelves() {
    const {
      shelves,
      user: { pages },
      params: { filter },
      currentUserLogin,
      app,
    } = this.props

    const shelvesPage = pages.shelves[filter]

    return (
      <InfiniteScroll load={this.load} page={shelvesPage}>
        <List>
          {shelves.map((shelf, i) => {
            return (
              <Shelf
                shelf={shelf}
                kind="list"
                showPlus={shelf.creator.login !== currentUserLogin}
                key={i}
                query={app.storedQuery}
              />
            )
          })}
        </List>
      </InfiniteScroll>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { username, filter } = params
    const { dispatch } = store

    const load: any[] = []

    if (username) {
      if (filter === 'following') {
        load.push(dispatch(loadShelvesFollowing(username)))
      } else {
        load.push(dispatch(loadShelves(username, 1, SHELVES_LIMIT)))
      }
    }

    await Promise.all(load)
  },
  {
    updateParams: 'filter',
    userListener: true,
  },
)

type Params = { filter: string }

const connectWrapper = connect((state: State, ownProps: { params: Params }) => {
  const {
    params: { filter = '' },
  } = ownProps
  return {
    app: state.app,
    user: buildUser(state, state.user.login),
    shelves: getShelvesByIds(state, state.user.shelves[filter]),
    currentUserLogin: state.currentUser.data.login,
  }
})

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(UserShelvesBox)
